import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Card,
  CardContent,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  DirectionsCar as RidesIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  AccessTime as TimeIcon,
  Logout as LogoutIcon,
  CardGiftcard as LoyaltyIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Refresh as ResetIcon,
  EmojiEvents as TrophyIcon,
  Redeem as RedeemIcon,
  Savings as SavingsIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

import {
  fetchCustomerById,
  deleteCustomer,
  logoutCustomer,
  addLoyaltyDiscount,
  removeLoyaltyDiscount,
  resetLoyaltyCard
} from '../../redux/slices/customersSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const InfoCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'visible',
}));

const CustomerAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  marginBottom: 16,
  border: `3px solid ${theme.palette.background.paper}`,
  backgroundColor: theme.palette.primary.main,
}));

const LabelTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
}));

const StatBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.grey[100],
}));

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentCustomer, isLoading, error } = useSelector(state => state.customers);
  const { global: globalSettings, isInitialized: settingsInitialized } = useSelector(state => state.settings);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    dispatch(fetchCustomerById(id));
    dispatch(fetchSettings());

    return () => {
      // Cleanup
    };
  }, [dispatch, id]);

  const handleEdit = () => {
    navigate(`/kunden/${id}/bearbeiten`);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    dispatch(deleteCustomer(id)).then((result) => {
      if (!result.error) {
        navigate('/kunden');
      }
    });
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    dispatch(logoutCustomer(id)).then((result) => {
      if (!result.error) {
        setSnackbar({ open: true, message: 'Kunde wurde abgemeldet', severity: 'success' });
      }
    });
    setLogoutDialogOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  // Treuekarten-Handler
  const handleAddDiscount = () => {
    dispatch(addLoyaltyDiscount({ customerId: id, amount: 1 })).then((result) => {
      if (!result.error) {
        setSnackbar({ open: true, message: 'Rabatt wurde hinzugefügt', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Fehler beim Hinzufügen', severity: 'error' });
      }
    });
  };

  const handleRemoveDiscount = () => {
    if (currentCustomer?.loyaltyCard?.availableDiscounts > 0) {
      dispatch(removeLoyaltyDiscount({ customerId: id, amount: 1 })).then((result) => {
        if (!result.error) {
          setSnackbar({ open: true, message: 'Rabatt wurde entfernt', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: 'Fehler beim Entfernen', severity: 'error' });
        }
      });
    }
  };

  const handleResetLoyaltyCard = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    dispatch(resetLoyaltyCard(id)).then((result) => {
      if (!result.error) {
        setSnackbar({ open: true, message: 'Treuekarte wurde zurückgesetzt', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Fehler beim Zurücksetzen', severity: 'error' });
      }
    });
    setResetDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Format timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Nie';
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (isLoading) {
    return <Loading message="Kundendaten werden geladen..." />;
  }

  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Kundendaten"
        message={error}
        onRetry={() => dispatch(fetchCustomerById(id))}
      />
    );
  }

  if (!currentCustomer) {
    return (
      <ErrorMessage
        title="Kunde nicht gefunden"
        message="Der angeforderte Kunde konnte nicht gefunden werden."
        onRetry={() => navigate('/kunden')}
      />
    );
  }

  // Treuekarten-Daten
  const loyaltyCard = currentCustomer.loyaltyCard || {
    currentRideCount: 0,
    availableDiscounts: 0,
    totalEarnedDiscounts: 0,
    totalRedeemedDiscounts: 0,
    totalSavingsEur: 0
  };
  const ridesRequired = globalSettings?.loyaltyCardRidesRequired || 5;
  const discountAmount = globalSettings?.loyaltyCardDiscountEur || 5;
  const loyaltyActive = globalSettings?.loyaltyCardActive || false;
  const progressPercent = (loyaltyCard.currentRideCount / ridesRequired) * 100;

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Kundendetails"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Kunden', path: '/kunden' },
          { label: currentCustomer.user?.name || 'Details', path: `/kunden/${id}` }
        ]}
        actionButton={{
          label: 'Bearbeiten',
          icon: <EditIcon />,
          onClick: handleEdit
        }}
      />

      <Grid container spacing={3}>
        {/* Linke Spalte - Kundeninfos */}
        <Grid item xs={12} md={4}>
          <InfoCard elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CustomerAvatar src={currentCustomer.user?.profile_picture || ''}>
                {currentCustomer.user?.name ? currentCustomer.user.name.charAt(0) : 'K'}
              </CustomerAvatar>
              <Typography variant="h5" gutterBottom>
                {currentCustomer.user?.name}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Chip
                  label={currentCustomer.isOnline ? 'Online' : 'Offline'}
                  color={currentCustomer.isOnline ? 'success' : 'default'}
                  icon={currentCustomer.isOnline ? <OnlineIcon /> : <OfflineIcon />}
                />
              </Box>
              <Divider sx={{ my: 2 }} />

              <List sx={{ width: '100%' }}>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>E-Mail</LabelTypography>}
                    secondary={currentCustomer.user?.email || 'Keine E-Mail'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Telefon</LabelTypography>}
                    secondary={currentCustomer.user?.phoneNumber || 'Keine Telefonnummer'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <RidesIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Fahrten</LabelTypography>}
                    secondary={`${currentCustomer.completed_rides || 0} abgeschlossen / ${currentCustomer.total_rides || 0} gesamt`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TimeIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={<LabelTypography>Letzter Login</LabelTypography>}
                    secondary={formatDate(currentCustomer.lastLogin)}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {currentCustomer.isOnline && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    fullWidth
                  >
                    Kunde abmelden
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  fullWidth
                >
                  Kunde löschen
                </Button>
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Rechte Spalte */}
        <Grid item xs={12} md={8}>
          {/* Treuekarten-Karte */}
          <InfoCard elevation={3}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LoyaltyIcon color="primary" />
                  <Typography variant="h6">
                    Treuekarten-Status
                  </Typography>
                </Box>
                <Chip
                  label={loyaltyActive ? 'System aktiv' : 'System inaktiv'}
                  color={loyaltyActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Divider sx={{ mb: 3 }} />

              {!loyaltyActive && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Das Treuekarten-System ist derzeit deaktiviert. Die Daten werden trotzdem angezeigt und können verwaltet werden.
                </Alert>
              )}

              {/* Fortschrittsanzeige */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Fortschritt zum nächsten Rabatt
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {loyaltyCard.currentRideCount} von {ridesRequired} Fahrten
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: progressPercent >= 100 ? 'success.main' : 'primary.main',
                      borderRadius: 5
                    }
                  }}
                />
                {progressPercent >= 80 && progressPercent < 100 && (
                  <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                    Nur noch {ridesRequired - loyaltyCard.currentRideCount} Fahrt(en) bis zum nächsten Rabatt!
                  </Typography>
                )}
              </Box>

              {/* Statistiken */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <RedeemIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {loyaltyCard.availableDiscounts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verfügbar
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({loyaltyCard.availableDiscounts * discountAmount}€)
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <TrophyIcon sx={{ fontSize: 32, mb: 1, color: 'gold' }} />
                    <Typography variant="h4" fontWeight="bold">
                      {loyaltyCard.totalEarnedDiscounts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Verdient
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <LoyaltyIcon sx={{ fontSize: 32, mb: 1, color: 'green' }} />
                    <Typography variant="h4" fontWeight="bold">
                      {loyaltyCard.totalRedeemedDiscounts}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Eingelöst
                    </Typography>
                  </StatBox>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatBox>
                    <SavingsIcon sx={{ fontSize: 32, mb: 1, color: 'green' }} />
                    <Typography variant="h4" fontWeight="bold">
                      {loyaltyCard.totalSavingsEur}€
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gespart
                    </Typography>
                  </StatBox>
                </Grid>
              </Grid>

              {/* Verwaltungs-Buttons */}
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Verwaltung
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Tooltip title="Rabatt manuell gutschreiben (z.B. als Entschuldigung)">
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={handleAddDiscount}
                  >
                    Rabatt hinzufügen
                  </Button>
                </Tooltip>
                <Tooltip title="Verfügbaren Rabatt entfernen">
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<RemoveIcon />}
                      onClick={handleRemoveDiscount}
                      disabled={loyaltyCard.availableDiscounts === 0}
                    >
                      Rabatt entfernen
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Alle Treuekarten-Daten zurücksetzen">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ResetIcon />}
                    onClick={handleResetLoyaltyCard}
                  >
                    Zurücksetzen
                  </Button>
                </Tooltip>
              </Box>

              {loyaltyCard.lastUpdated && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Letzte Aktualisierung: {formatDate(loyaltyCard.lastUpdated)}
                </Typography>
              )}
            </CardContent>
          </InfoCard>

          {/* Fahrtenhistorie */}
          <InfoCard elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fahrtenhistorie
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {currentCustomer.total_rides > 0 ? (
                <Typography variant="body1">
                  Anzahl der Fahrten: {currentCustomer.total_rides} (davon {currentCustomer.completed_rides || 0} abgeschlossen)
                </Typography>
              ) : (
                <Typography variant="body1">
                  Dieser Kunde hat noch keine Fahrten gebucht.
                </Typography>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to={`/fahrten?customerId=${id}`}
                >
                  Alle Fahrten anzeigen
                </Button>
              </Box>
            </CardContent>
          </InfoCard>

          {/* Kundeninformationen */}
          <InfoCard elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kundeninformationen
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Kunde-ID</LabelTypography>
                  <Typography variant="body1" gutterBottom>{id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Name</LabelTypography>
                  <Typography variant="body1" gutterBottom>{currentCustomer.user?.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">E-Mail</LabelTypography>
                  <Typography variant="body1" gutterBottom>{currentCustomer.user?.email}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Telefon</LabelTypography>
                  <Typography variant="body1" gutterBottom>
                    {currentCustomer.user?.phoneNumber || 'Keine Telefonnummer'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Online Status</LabelTypography>
                  <Typography variant="body1" gutterBottom>
                    <Chip
                      size="small"
                      label={currentCustomer.isOnline ? 'Online' : 'Offline'}
                      color={currentCustomer.isOnline ? 'success' : 'default'}
                    />
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LabelTypography variant="subtitle2">Zuletzt Online</LabelTypography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(currentCustomer.lastLogin)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </InfoCard>
        </Grid>
      </Grid>

      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Kunde löschen"
        content={`Möchten Sie den Kunden ${currentCustomer.user?.name} wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Logout-Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
      >
        <DialogTitle>Kunde abmelden</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie den Kunden {currentCustomer.user?.name} wirklich abmelden?
            Der Kunde wird in der App ausgeloggt und muss sich neu anmelden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Abbrechen</Button>
          <Button onClick={handleLogoutConfirm} color="warning" variant="contained">
            Abmelden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset-Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
      >
        <DialogTitle>Treuekarte zurücksetzen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Treuekarte von {currentCustomer.user?.name} wirklich zurücksetzen?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Alle Treuekarten-Daten (Fortschritt, verfügbare Rabatte, Statistiken) werden gelöscht.
            Dieser Vorgang kann nicht rückgängig gemacht werden.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleResetConfirm} color="error" variant="contained">
            Zurücksetzen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerDetails;
